function _typeof(o) { "@babel/helpers - typeof"; return _typeof = "function" == typeof Symbol && "symbol" == typeof Symbol.iterator ? function (o) { return typeof o; } : function (o) { return o && "function" == typeof Symbol && o.constructor === Symbol && o !== Symbol.prototype ? "symbol" : typeof o; }, _typeof(o); }
function _defineProperty(obj, key, value) { key = _toPropertyKey(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey(arg) { var key = _toPrimitive(arg, "string"); return _typeof(key) === "symbol" ? key : String(key); }
function _toPrimitive(input, hint) { if (_typeof(input) !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (_typeof(res) !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
import _extends from "@babel/runtime-corejs3/helpers/esm/extends";
import _mapInstanceProperty from "@babel/runtime-corejs3/core-js/instance/map";
import _includesInstanceProperty from "@babel/runtime-corejs3/core-js/instance/includes";
import React from "react";
import PropTypes from "prop-types";
import cx from "classnames";
import classesPropType from "@xo-union/classes-prop-type";
import { FocusBoundary } from "@xo-union/ui-accessibility";
import { useClasses } from "@xo-union/react-css-modules";
import { useUniqueID } from "@xo-union/react-unique-id";
import useNavigation from "../useNavigation/index.js";
import getSlideTranslate from "../getSlideTranslate/index.js";
import ScreenReaderDescription from "../ScreenReaderDescription/index.js";
import getAlignmentClassName from "../getAlignmentClassName/index.js";
import useDraggable from "../useDraggable/index.js";
import getTransitionClassName from "../getTransitionClassName/index.js";
import { humanTermType, singular } from "../humanTerms.js";
import styles from "./styles.css";
import useFallbackRef from "../useFallbackRef/index.js";

/**
 * Carousel container.
 */
var BaseCarousel = function BaseCarousel(props) {
  var _context;
  var NextButton = props.renderNextButton,
    PrevButton = props.renderPrevButton,
    classesProp = props.classes,
    id = props.id,
    srDescriptionID = props.srDescriptionID,
    slideIDPrefixProp = props.slideIDPrefix,
    terms = props.terms,
    children = props.children,
    slideRole = props.slideRole,
    carouselRole = props.carouselRole,
    listRole = props.listRole,
    getAlignment = props.alignment,
    navigationIndex = props.navigationIndex,
    navigationIndexDefaultValue = props.navigationIndexDefaultValue,
    onNavigationIndexChange = props.onNavigationIndexChange,
    onNavigationRangeChange = props.onNavigationRangeChange,
    pagination = props.pagination,
    gutter = props.gutter,
    _props$internals = props.internals,
    givenContainerRef = _props$internals.containerRef,
    totalSlidesCount = _props$internals.totalSlidesCount,
    visibleSlidesCount = _props$internals.visibleSlidesCount,
    isReadyToRender = _props$internals.isReadyToRender,
    slideWidthValue = _props$internals.slideWidthValue,
    extraPixels = _props$internals.extraPixels;
  var containerRef = useFallbackRef(givenContainerRef);
  var hasSlideOverflow = totalSlidesCount > visibleSlidesCount;
  var navigationProps = {
    navigationIndex: navigationIndex,
    navigationIndexDefaultValue: navigationIndexDefaultValue,
    onNavigationIndexChange: onNavigationIndexChange,
    onNavigationRangeChange: onNavigationRangeChange,
    pagination: pagination
  };
  var classes = useClasses(styles, classesProp);
  var slideIDPrefix = useUniqueID(slideIDPrefixProp);
  var slideContainerID = useUniqueID(id);
  var screenReaderDescriptionID = useUniqueID(srDescriptionID);
  var navigation = useNavigation(navigationProps, {
    visibleSlidesCount: visibleSlidesCount,
    totalSlidesCount: totalSlidesCount
  });
  var draggableProps = useDraggable({
    navigation: navigation
  });
  var alignment = getAlignment({
    currentIndex: navigation.committedNavigationIndex,
    hasSlideOverflow: hasSlideOverflow,
    minIndex: navigation.navigationIndexRange.min,
    maxIndex: navigation.navigationIndexRange.max
  });
  var slideTranslateValue = getSlideTranslate({
    visualOffset: navigation.visualNavigationOffset,
    alignment: alignment,
    visibleSlidesCount: visibleSlidesCount,
    extraPixels: extraPixels
  });
  var halfGutter = "".concat(gutter ? gutter / 2 : 0, "px");
  var finalListRole = listRole || (visibleSlidesCount === 1 ? 'presentation' : 'list');
  var finalListItemRole = finalListRole === 'list' ? 'listitem' : 'presentation';
  return /*#__PURE__*/React.createElement("div", {
    style: {
      '--carousel-half-gutter': halfGutter,
      '--carousel-slide-width': slideWidthValue,
      '--carousel-slide-translate': slideTranslateValue
    },
    className: cx(classes.relativeContainer, getAlignmentClassName(alignment, classes), getTransitionClassName(navigation.animationState, classes), _defineProperty({}, classes.notReady, !isReadyToRender))
  }, /*#__PURE__*/React.createElement("div", {
    ref: containerRef,
    className: classes.container,
    role: carouselRole,
    id: slideContainerID,
    "aria-roledescription": "carousel",
    "aria-label": singular(terms.carousel),
    "aria-describedby": screenReaderDescriptionID
  }, /*#__PURE__*/React.createElement(ScreenReaderDescription, {
    id: screenReaderDescriptionID,
    containerRef: containerRef,
    visibleSlidesIndexRange: navigation.visibleSlidesIndexRange,
    terms: terms,
    totalSlidesCount: totalSlidesCount
  }), /*#__PURE__*/React.createElement("div", {
    className: classes.leftButtonContainer
  }, /*#__PURE__*/React.createElement(PrevButton, {
    "aria-controls": "".concat(slideContainerID, " ").concat(screenReaderDescriptionID),
    "aria-label": "See previous ".concat(singular(terms.carousel), " page"),
    className: cx('prevButton' in classes && classes.prevButton, !navigation.canMove(-1) && classes.hideButton),
    disabled: !navigation.canMove(-1),
    onClick: navigation.moveLeft
  })), /*#__PURE__*/React.createElement("ul", _extends({
    role: finalListRole
  }, draggableProps, {
    onTransitionEnd: navigation.resetAnimation,
    className: cx(draggableProps.className, classes.slidesList)
  }), _mapInstanceProperty(_context = React.Children).call(_context, children, function (child, i) {
    var _context2;
    var isVisible = _includesInstanceProperty(_context2 = navigation.visibleSlidesIndexRange).call(_context2, i);
    return /*#__PURE__*/React.createElement(FocusBoundary, {
      enabled: isVisible
    }, /*#__PURE__*/React.createElement("li", {
      role: finalListItemRole,
      "aria-hidden": isVisible ? undefined : true,
      className: classes.slideContainer
    }, /*#__PURE__*/React.createElement("div", {
      id: [slideIDPrefix, i].join('--'),
      role: slideRole,
      "aria-roledescription": "slide",
      "aria-label": singular(terms.slides),
      className: classes.slide
    }, child)));
  })), /*#__PURE__*/React.createElement("div", {
    className: classes.rightButtonContainer
  }, /*#__PURE__*/React.createElement(NextButton, {
    "aria-controls": "".concat(slideContainerID, " ").concat(screenReaderDescriptionID),
    "aria-label": "See next ".concat(singular(terms.carousel), " page"),
    className: cx('nextButton' in classes && classes.nextButton, !navigation.canMove(1) && classes.hideButton),
    disabled: !navigation.canMove(1),
    onClick: navigation.moveRight
  }))));
};
process.env.NODE_ENV !== "production" ? BaseCarousel.propTypes = {
  gutter: PropTypes.number,
  internals: PropTypes.shape({
    containerRef: PropTypes.shape({
      current: PropTypes.shape({})
    }),
    totalSlidesCount: PropTypes.number,
    isReadyToRender: PropTypes.bool,
    slideWidthValue: PropTypes.string,
    visibleSlidesCount: PropTypes.number,
    extraPixels: PropTypes.number
  }),
  /**
   * A mapper function that receives the navigation state and returns 'left', 'center', 'right'
   */
  alignment: PropTypes.func,
  /**
   * Render the previous button
   */
  renderPrevButton: PropTypes.func,
  /**
   * Render the next button
   */
  renderNextButton: PropTypes.func,
  /**
   * Slides. Each child is wrapped in a slide container.
   */
  children: PropTypes.node,
  /**
   * Classes
   */
  classes: classesPropType(['relativeContainer', 'container', 'slide', 'slideContainer', 'slidesList', 'prevButton', 'nextButton']),
  /**
   * Override carousel container id
   */
  id: PropTypes.string,
  /**
   * Override ID of screen reader description element
   */
  srDescriptionID: PropTypes.string,
  /**
   * Human terms used to descrive the carousel and slides. This is to provide a better screen reader experience
   *
   * Examples:
   * ```json
   *  {
   *     "carousel": "Product catalog",
   *     "slides": {"plural": "Products", "singular": "Product"}
   *  }
   * ```
   */
  terms: humanTermType(['slides', 'carousel']),
  /**
   * Aria role to assign to slide. "group" is the default, and should be used in most general cases. "tabpanel" should be used when every other slide is not visible, additionally, if there is are tabs used to control the slides. See example here:
   *
   *  https://w3c.github.io/aria-practices/examples/carousel/carousel-2-tablist.html
   */
  slideRole: PropTypes.oneOf(['group', 'tabpanel']),
  /**
   * Aria role to assign to carousel. "group" is the default. This should be used when multiple instances of the carousel will be rendered on the page. However, you can/should use "region" when the carousel takes up a significant amount of space on the page, or when you have a single carousel on the page and want to identify it as a major landmark.
   */
  carouselRole: PropTypes.oneOf(['group', 'region']),
  /**
   * Aria role assignable to list. By default role is "list" but it becomes "presentation" when there is only one item visible. You can override this behavior by specifying the role for this element
   */
  listRole: PropTypes.oneOf(['presentation', 'list']),
  /**
   * Override prefix used for each slide id
   */
  slideIDPrefix: PropTypes.string,
  /**
   * Page size used to determine how many slides to move on left and right button clicks. When set to `'auto'` it is dynamically adjusted to be the number of visible slides
   */
  pagination: PropTypes.oneOfType([PropTypes.oneOf(['auto']), PropTypes.number]),
  /**
   * Callback called before the navigation index changes
   *
   * This can be due to user dragging the slides, clicking on the navigation buttons,
   * or even resizing the screen.
   */
  onNavigationIndexChange: PropTypes.func,
  /**
   * Callback called after carousel determines the navigation bounds/range of the carousel also
   * known as the min and max index allowed, while respecting the visual requirements of the carousel
   * (not allowing empty spaces on the left or right side of the carousel)
   */
  onNavigationRangeChange: PropTypes.func,
  /**
   * Number to control the navigation index
   */
  navigationIndex: PropTypes.number,
  /**
   * The initial value for the navigation index on uncontrolled mode
   */
  navigationIndexDefaultValue: PropTypes.number
} : void 0;
BaseCarousel.defaultProps = {
  slideRole: 'group',
  carouselRole: 'group',
  renderPrevButton: function renderPrevButton() {
    return null;
  },
  renderNextButton: function renderNextButton() {
    return null;
  },
  terms: {
    slides: {
      plural: 'slides',
      singular: 'slide'
    },
    carousel: 'carousel'
  }
};
export default BaseCarousel;